import React, { useEffect, useMemo } from "react";
import { useLocalContext } from "../../context/LocalContext";

const TAGS = ["Instant digital access", "Email delivery", "No physical shipping"];

const ShippingPolicy = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo?.name || "Our Store";

  useEffect(() => {
    if (brand) {
      document.title = `Shipping & Digital Delivery Policy | ${brand}`;
    }
  }, [brand]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, []);

  const sections = [
    { id: "format", title: "How We Deliver Your Order" },
    { id: "times", title: "Processing & Delivery Timeframes" },
    { id: "responsibility", title: "Your Details & Responsibilities" },
    { id: "activation", title: "Activation Help & After-Sales Support" },
    { id: "no-physical", title: "No Physical Shipping or Parcels" },
    { id: "refunds", title: "Delays, Refunds & Cancellations" },
    { id: "assistance", title: "Contact & Delivery Assistance" },
  ];

  // Best-effort website URL
  let siteUrl = "";
  if (typeof window !== "undefined") {
    siteUrl = window.location.origin;
  }

  return (
    <div className="t-page">
      {/* Hero */}
      <header className="t-hero">
        <div className="t-hero__eyebrow">LEGAL</div>
        <h1 className="t-hero__title">Shipping &amp; Digital Delivery Policy</h1>
        <p className="t-hero__subtitle">
          This page explains how digital products are delivered, typical
          timeframes, what to do if an email doesn’t arrive, and how we support
          you with activation or access issues.
        </p>
        <div className="t-hero__meta">Last updated: {lastUpdated}</div>

        <div className="t-hero__tags" aria-label="Key points">
          {TAGS.map((t) => (
            <span key={t} className="t-pill">
              {t}
            </span>
          ))}
        </div>
      </header>

      {/* Grid */}
      <section className="t-grid">
        {/* Sidebar */}
        <aside className="t-toc" aria-label="On this page">
          <div className="t-card t-toc__card">
            <div className="t-toc__title">ON THIS PAGE</div>
            <nav>
              <ol className="t-toc__list">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>
                      <span className="t-toc__num">{i + 1})</span> {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="t-card t-content">
          {/* 1 */}
          <section id="format" className="t-sec">
            <h2 className="t-h2">How We Deliver Your Order</h2>
            <p>
              All products sold by <strong>{brand}</strong> are{" "}
              <strong>digital-only</strong> — typically license keys,
              subscription codes, account access details, or official download
              instructions.
            </p>
            <p>After your payment is successfully processed, you will usually receive:</p>
            <ul className="t-list">
              <li>
                An <strong>order confirmation email</strong> with your license
                key or secure access link (where supported).
              </li>
              <li>
                Any relevant <strong>download links</strong> or references to
                official vendor pages where the software/app can be obtained.
              </li>
              <li>
                A short <strong>activation or setup guide</strong> outlining the
                basic steps to get started.
              </li>
            </ul>
            <p>
              If your order contains multiple products or bundles, some items
              may be delivered in <strong>separate emails</strong> or in
              separate sections of your account/order page.
            </p>
          </section>

          {/* 2 */}
          <section id="times" className="t-sec">
            <h2 className="t-h2">Processing &amp; Delivery Timeframes</h2>
            <p>
              We aim to send most digital orders{" "}
              <strong>within a short period after payment is confirmed</strong>{" "}
              (often within a few minutes, depending on payment provider and
              system load).
            </p>
            <p>
              In some situations, processing may take longer, sometimes up to{" "}
              <strong>24 hours or more</strong>, for example due to:
            </p>
            <ul className="t-list">
              <li>Additional payment or anti-fraud checks by the gateway.</li>
              <li>Temporary stock syncing or licensing system delays.</li>
              <li>Vendor API outages or scheduled maintenance.</li>
              <li>Weekends, public holidays, or unusual order volume.</li>
            </ul>
            <p>If your license or access email has not arrived as expected:</p>
            <ul className="t-list">
              <li>
                Check your <strong>Spam, Junk, Promotions</strong> and similar
                folders and search for “{brand}”.
              </li>
              <li>
                Make sure the email address you used at checkout is spelled
                correctly and can receive new messages.
              </li>
              <li>
                If you still cannot find it, contact our support team with your{" "}
                <strong>order number</strong> so we can investigate and, if
                needed, resend or re-issue your details.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section id="responsibility" className="t-sec">
            <h2 className="t-h2">Your Details &amp; Responsibilities</h2>
            <p>To help ensure smooth digital delivery, you agree to:</p>
            <ul className="t-list">
              <li>
                Provide a <strong>valid, accessible email address</strong> at
                checkout and keep it up to date.
              </li>
              <li>
                Make sure your mailbox is able to receive messages (not full,
                not blocking our domain, and not auto-filtering everything to
                spam).
              </li>
              <li>
                Inform us promptly if you notice a mistake in your email or
                contact details linked to an order.
              </li>
              <li>
                Store your license keys or access details securely once
                delivered, as for security reasons we may not always display
                them repeatedly.
              </li>
            </ul>
            <p>
              <strong>{brand}</strong> cannot be responsible for delivery
              failures or delays caused by incorrect email addresses, blocking
              by your email provider, or mail settings beyond our control.
            </p>
          </section>

          {/* 4 */}
          <section id="activation" className="t-sec">
            <h2 className="t-h2">Activation Help &amp; After-Sales Support</h2>
            <p>
              Every product includes at least basic instructions on how to
              install and activate the software or service. If you encounter:
            </p>
            <ul className="t-list">
              <li>Messages like “key already used” or “invalid key”.</li>
              <li>Region, account, or device compatibility warnings.</li>
              <li>Issues downloading, installing, or signing in.</li>
            </ul>
            <p>please contact our support team and share:</p>
            <ul className="t-list">
              <li>Your order number or invoice reference.</li>
              <li>Clear screenshots of any error messages.</li>
              <li>
                Basic system details (operating system, region, and any existing
                security/antivirus tools).
              </li>
            </ul>
            <p>We will then:</p>
            <ul className="t-list">
              <li>Check our internal logs and order records.</li>
              <li>
                Where needed, verify the license with the publisher or
                distributor.
              </li>
              <li>
                Provide step-by-step guidance, a{" "}
                <strong>replacement key</strong> if confirmed defective or
                mis-issued, or discuss alternatives such as a refund or store
                credit in line with our{" "}
                <a href="/refund-policy">Refund &amp; Return Policy</a>.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section id="no-physical" className="t-sec">
            <h2 className="t-h2">No Physical Shipping or Parcels</h2>
            <p>
              <strong>{brand}</strong> does{" "}
              <strong>not send any physical goods</strong>. There are no printed
              boxes, CDs/DVDs, USB drives, or courier deliveries connected to
              your purchase.
            </p>
            <p>
              If you see references to “shipping”, “dispatch”, “tracking”, or
              similar wording in generic templates on partner platforms, they
              should be understood as referring only to{" "}
              <strong>electronic delivery</strong> (email or online account
              access), not physical courier services.
            </p>
          </section>

          {/* 6 */}
          <section id="refunds" className="t-sec">
            <h2 className="t-h2">Delays, Refunds &amp; Cancellations</h2>
            <p>
              Because license keys are digital and cannot be “returned” once
              they are shown to you,{" "}
              <strong>
                most fulfilled orders are not eligible for a change of mind
                refund
              </strong>
              , unless stated otherwise in our{" "}
              <a href="/refund-policy">Refund &amp; Return Policy</a>.
            </p>
            <ul className="t-list">
              <li>
                If you contact us <strong>before</strong> a key is generated or
                revealed, we will review cancellation requests individually.
              </li>
              <li>
                Where there are significant delivery delays clearly caused by
                our systems (not by email providers, spam filters, or
                third-party infrastructure), we will work with you to complete
                delivery or discuss a suitable solution.
              </li>
              <li>
                Duplicate or accidental orders should be reported{" "}
                <strong>as quickly as possible</strong>, ideally before any
                keys are issued, so that we can review next steps.
              </li>
            </ul>
          </section>

          {/* 7 */}
          <section id="assistance" className="t-sec">
            <h2 className="t-h2">Contact &amp; Delivery Assistance</h2>
            <p>
              If you have questions about how your order is delivered, cannot
              find your email, or need help with activation, you can reach us
              using the details below:
            </p>
            <address className="t-address">
              <strong>{brand}</strong>
              <br />
              {webinfo.address && (
                <>
                  {webinfo.address}
                  <br />
                </>
              )}
              Email:{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              <br />
              {webinfo.phone && (
                <>
                  Phone: <a href={`tel:${webinfo.phone}`}>{webinfo.phone}</a>
                  <br />
                </>
              )}
              {siteUrl && (
                <>
                  Website:{" "}
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteUrl}
                  </a>
                </>
              )}
            </address>
          </section>
        </article>
      </section>
    </div>
  );
};

export default ShippingPolicy;
